import type {
  InviteMemberRequest,
  SettingsSnapshot,
  TeamMember,
  TeamMemberRef,
  UpdateSettingsRequest,
  UpdateTeamMemberRequest,
} from '@/data-sources/contract';
import { api, overrideExisting } from '@/store/api/api';
import { fromSource, type Sourced } from '@/store/api/fromSource';
import { tag } from '@/store/api/tags';

export const settingsApi = api.injectEndpoints({
  overrideExisting,
  endpoints: (build) => ({
    getSettings: build.query<SettingsSnapshot, Sourced<void>>({
      queryFn: fromSource((source, _params, ctx) => source.getSettings(ctx)),
      providesTags: (_r, _e, arg) => [tag('Settings', arg.source), tag('Team', arg.source)],
    }),
    updateSettings: build.mutation<SettingsSnapshot, Sourced<UpdateSettingsRequest>>({
      queryFn: fromSource((source, params, ctx) => source.updateSettings(params, ctx)),
      invalidatesTags: (_r, e, arg) => (e ? [] : [tag('Settings', arg.source)]),
    }),
    inviteMember: build.mutation<TeamMember, Sourced<InviteMemberRequest>>({
      queryFn: fromSource((source, params, ctx) => source.inviteMember(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Settings', arg.source), tag('Team', arg.source)],
    }),
    updateTeamMember: build.mutation<TeamMember, Sourced<UpdateTeamMemberRequest>>({
      queryFn: fromSource((source, params, ctx) => source.updateTeamMember(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Settings', arg.source), tag('Team', arg.source)],
    }),
    removeTeamMember: build.mutation<null, Sourced<TeamMemberRef>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.removeTeamMember(params, ctx);
        return null;
      }),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Settings', arg.source), tag('Team', arg.source)],
    }),
    revokeInvitation: build.mutation<null, Sourced<TeamMemberRef>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.revokeInvitation(params, ctx);
        return null;
      }),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Settings', arg.source), tag('Team', arg.source)],
    }),
  }),
});
export const {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useInviteMemberMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useRevokeInvitationMutation,
} = settingsApi;
